import { cn } from '@tempo-io/sui-class-utils';
import { useCallback, useRef, useState } from 'react';

const SCROLL_SPEED_PX_PER_SEC = 40;
const MIN_SCROLL_DURATION_SEC = 1.5;

export function TimelineItemName({
  name,
  color,
  fontSize,
  alignNameLeft,
}: {
  name: string;
  color: string;
  fontSize: number;
  alignNameLeft?: boolean;
}) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const [overflow, setOverflow] = useState(0);

  const onMouseEnter = useCallback(() => {
    if (!titleRef.current || !wrapperRef.current) return;
    const style = getComputedStyle(wrapperRef.current);
    const horizontalPadding = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
    const visibleWidth = wrapperRef.current.clientWidth - horizontalPadding;
    const distance = titleRef.current.scrollWidth - visibleWidth;
    if (distance > 1) setOverflow(distance);
  }, []);

  const onMouseLeave = useCallback(() => setOverflow(0), []);

  const isScrolling = overflow > 0;

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: hover toggles marquee scroll only
    <div
      ref={wrapperRef}
      className={cn(
        'px-2 font-semibold flex-1 min-w-0 overflow-hidden',
        alignNameLeft && 'text-left',
      )}
      style={{ color, fontSize }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div
        ref={titleRef}
        className={cn('whitespace-nowrap', !isScrolling && 'overflow-hidden text-ellipsis')}
        style={
          isScrolling
            ? {
                animation: `timeline-name-bounce ${Math.max(overflow / SCROLL_SPEED_PX_PER_SEC, MIN_SCROLL_DURATION_SEC)}s linear infinite alternate`,
                ['--timeline-name-overflow' as string]: `-${overflow}px`,
              }
            : undefined
        }
      >
        {name}
      </div>
    </div>
  );
}
